import { Box, CircularProgress } from '@material-ui/core';
import {
  AgentInLenderContext,
  AgentInSinglePaymentProviderContext,
  deleteAgentInLenderContextById,
  deleteAgentInSinglePaymentProviderContextById,
  getAgentInLenderContextByAgentId,
  getAgentInSinglePaymentProviderContextByAgentId,
  getMerchantStructureByMerchantId,
  Lender,
} from '@nemuru-eng/js-api-client';
import { Dispatch, SetStateAction } from 'react';

import { AgentInLenderContextTable } from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/components/AgentInLenderContextTable/AgentInLenderContextTable';
import { AgentInSinglePaymentProviderContextTable } from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/components/AgentInSinglePaymentProviderContextTable/AgentInSinglePaymentProviderContextTable';
import { CredentialsType } from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/types/CredentialsType';
import { Font } from '@/components/shared/components/Font';
import { NemuruDialog } from '@/components/shared/components/NemuruDialog';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useCache } from '@/hooks/requests/useCache';
import { useRequest } from '@/hooks/requests/useRequest';
interface DeleteCredentialsDialogProps {
  opened: boolean;
  setOpened: Dispatch<SetStateAction<boolean>>;
  toggleDialog;
  agentId: string;
  channelId: string;
  merchantId: string;
  lenders: Lender[];
  selectedAgentCredentials: (AgentInLenderContext | AgentInSinglePaymentProviderContext)[];
  credentialsType: CredentialsType;
}

export const DeleteCredentialsDialog = ({
  opened,
  setOpened,
  toggleDialog,
  agentId,
  channelId,
  merchantId,
  lenders,
  selectedAgentCredentials,
  credentialsType,
}: DeleteCredentialsDialogProps) => {
  const { request, loading } = useRequest();
  const { revalidate } = useCache(getAgentInLenderContextByAgentId, { agentId }, { fallbackData: [] });
  const { revalidate: revalidateAgentInSinglePaymentProvider } = useCache(
    getAgentInSinglePaymentProviderContextByAgentId,
    { agentId },
    { fallbackData: [] },
  );
  const { data: merchantStructure } = useCache(getMerchantStructureByMerchantId, { merchantId });

  const onDeleteCredentials = async () => {
    try {
      if (credentialsType === CredentialsType.AGENT_IN_LENDER_CONTEXT) {
        await request(deleteAgentInLenderContextById, {
          id: selectedAgentCredentials[0].id,
        });
        await revalidate();
      }
      if (credentialsType === CredentialsType.AGENT_IN_SINGLE_PAYMENT_PROVIDER_CONTEXT) {
        await request(deleteAgentInSinglePaymentProviderContextById, {
          id: selectedAgentCredentials[0].id,
        });
        await revalidateAgentInSinglePaymentProvider();
      }
    } catch (error) {
    } finally {
      toggleDialog();
    }
  };

  if (!merchantStructure) return <></>;
  return (
    <NemuruDialog
      avoidDefaultClose
      opened={opened}
      setOpened={setOpened}
      buttons={
        <>
          <StandardButton disabled={loading} onClick={toggleDialog}>
            <Text tId={'CANCEL'} />
          </StandardButton>
          <StandardButton disabled={loading} main onClick={() => onDeleteCredentials()}>
            {loading ? <CircularProgress size={20} color={'secondary'} /> : <Text tId="DELETE" />}
          </StandardButton>
        </>
      }
      dialogProps={{
        maxWidth: 'md',
        fullWidth: true,
      }}
    >
      <>
        <Box mb={5}>
          <Font variant={'h3'} color={'title'}>
            <Text tId={'DELETE_CREDENTIALS'} />
          </Font>
        </Box>
        <Box>
          <Font variant={'body2'} color={'textSecondary'}>
            <Text tId={'THE_FOLLOWING_CREDENTIALS_WILL_BE_DELETED'} />
          </Font>
        </Box>
        <Box mt={4}>
          {credentialsType === CredentialsType.AGENT_IN_LENDER_CONTEXT &&
            selectedAgentCredentials.every(
              (credential): credential is AgentInLenderContext => 'lenderId' in credential,
            ) &&
            selectedAgentCredentials.length > 0 && (
              <AgentInLenderContextTable
                agentId={agentId}
                lenders={lenders}
                agentsInLenderContextCredentials={selectedAgentCredentials}
                hideColumnsForDeleteMode={true}
                channelId={channelId}
                credentialsType={credentialsType}
                merchantStructure={merchantStructure}
              />
            )}
          {credentialsType === CredentialsType.AGENT_IN_SINGLE_PAYMENT_PROVIDER_CONTEXT &&
            selectedAgentCredentials.every(
              (credential): credential is AgentInSinglePaymentProviderContext => 'providerId' in credential,
            ) && (
              <AgentInSinglePaymentProviderContextTable
                agentId={agentId}
                lenders={lenders}
                agentsInSinglePaymentProviderContext={selectedAgentCredentials}
                hideColumnsForDeleteMode={true}
                channelId={channelId}
                credentialsType={credentialsType}
                merchantStructure={merchantStructure}
              />
            )}
        </Box>
      </>
    </NemuruDialog>
  );
};
